/**
 * New-contract modal.
 *
 * Captures the full contract create input — common fields plus a
 * type-conditional block (SLA / BAU / Internal). Single submit posts
 * to /api/contracts which validates the discriminated-union shape.
 *
 * SPEC §9.3 says contract terms are immutable after activation, so
 * the only way to change e.g. monthly_hours is to create a new
 * contract. This screen is the right place to nail those numbers.
 *
 * Currency is AUD; overage / top-up rates are entered in dollars and
 * the form converts to cents at submit (the schema is integer cents).
 */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import type { Contract, Organization } from '@beacon/core';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { useCreateContract } from '@/hooks/use-contracts.js';
import { useAssignableUsers } from '@/hooks/use-users.js';

interface Props {
  /** Null hides the modal; the parent is the only state source. */
  organization: Organization | null;
  /**
   * Optional source contract to duplicate. SPEC §9 calls this the
   * 90% case — most new contracts are renewals or near-clones of an
   * existing one. When set, all term fields prefill from the source;
   * name gets a "(copy)" suffix; start/end dates are left blank
   * because those must be set intentionally per renewal.
   */
  duplicateFrom?: Contract | null;
  onClose: () => void;
}

type ContractKind = 'sla' | 'warranty' | 'bau' | 'internal';

export function NewContractDialog({
  organization,
  duplicateFrom,
  onClose,
}: Props) {
  const create = useCreateContract();
  const usersQuery = useAssignableUsers();

  const isDuplicating = Boolean(duplicateFrom);

  // Common fields
  const [type, setType] = useState<ContractKind>('sla');
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [accountOwnerUserId, setAccountOwnerUserId] = useState('');
  const [slackChannel, setSlackChannel] = useState('');
  const [harvestProjectId, setHarvestProjectId] = useState('');

  // SLA fields
  const [monthlyHours, setMonthlyHours] = useState('');
  const [overageRateAud, setOverageRateAud] = useState('');
  const [responseMinutes, setResponseMinutes] = useState('');
  const [solutionMinutes, setSolutionMinutes] = useState('');
  const [businessHoursOnly, setBusinessHoursOnly] = useState(true);
  const [pauseOutsideHours, setPauseOutsideHours] = useState(true);

  // BAU fields
  const [totalHours, setTotalHours] = useState('');
  const [durationMonths, setDurationMonths] = useState('');
  const [topUpsAllowed, setTopUpsAllowed] = useState(true);
  const [topUpRateAud, setTopUpRateAud] = useState('');

  useEffect(() => {
    if (!organization) return;
    if (duplicateFrom) {
      // Duplicate flow — prefill everything except dates (must be set
      // intentionally per renewal) from the source contract.
      const src = duplicateFrom;
      setType(src.type);
      setName(`${src.name} (copy)`);
      setStartDate('');
      setEndDate('');
      setAccountOwnerUserId(src.accountOwnerUserId);
      setSlackChannel(src.slackChannel ?? '');
      setHarvestProjectId(src.harvestProjectId ?? '');
      if (src.type === 'sla' || src.type === 'warranty') {
        setMonthlyHours(String(src.monthlyHours));
        setOverageRateAud(
          src.overageRateCents
            ? (src.overageRateCents / 100).toFixed(2)
            : ''
        );
        setResponseMinutes(String(src.responseTargetMinutes));
        setSolutionMinutes(String(src.solutionTargetMinutes));
        setBusinessHoursOnly(src.businessHoursOnly);
        setPauseOutsideHours(src.pauseOutsideHours);
      } else if (src.type === 'bau') {
        setTotalHours(String(src.totalHours));
        setDurationMonths(String(src.durationMonths));
        setTopUpsAllowed(src.topUpsAllowed);
        setTopUpRateAud(
          src.topUpRateCents
            ? (src.topUpRateCents / 100).toFixed(2)
            : ''
        );
      }
      return;
    }
    // From-scratch — sensible defaults.
    setType('sla');
    setName(`${organization.name} SLA`);
    setStartDate('');
    setEndDate('');
    setAccountOwnerUserId('');
    setSlackChannel('');
    setHarvestProjectId('');
    setMonthlyHours('');
    setOverageRateAud('');
    setResponseMinutes('60');
    setSolutionMinutes('480');
    setBusinessHoursOnly(true);
    setPauseOutsideHours(true);
    setTotalHours('');
    setDurationMonths('');
    setTopUpsAllowed(true);
    setTopUpRateAud('');
  }, [organization, duplicateFrom]);

  useEffect(() => {
    if (!organization) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !create.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [organization, create.isPending, onClose]);

  // When type changes, prefill name suffix as a hint.
  useEffect(() => {
    if (!organization) return;
    const base = organization.name;
    const map: Record<ContractKind, string> = {
      sla: ` SLA`,
      warranty: ` Warranty`,
      bau: ` BAU`,
      internal: ` (Internal)`,
    };
    setName((current) =>
      current === `${base} SLA` ||
      current === `${base} BAU` ||
      current === `${base} (Internal)`
        ? `${base}${map[type]}`
        : current
    );
  }, [type, organization]);

  if (!organization) return null;

  function handleSubmit() {
    if (!name.trim()) return toast.error('Name is required');
    if (!startDate || !endDate)
      return toast.error('Both start and end dates are required');
    if (startDate >= endDate)
      return toast.error('Start date must be before end date');
    if (!accountOwnerUserId) return toast.error('Pick an account owner');

    const slack = slackChannel.trim() || null;
    const harvest = harvestProjectId.trim() || null;
    const baseInput = {
      organizationId: organization!.id,
      name: name.trim(),
      startDate,
      endDate,
      accountOwnerUserId,
      slackChannel: slack,
      logoUrl: null,
      harvestProjectId: harvest,
    };

    if (type === 'sla' || type === 'warranty') {
      const mh = parseInt(monthlyHours, 10);
      const rt = parseInt(responseMinutes, 10);
      const st = parseInt(solutionMinutes, 10);
      if (!Number.isFinite(mh) || mh <= 0)
        return toast.error('Monthly hours must be a positive integer');
      if (!Number.isFinite(rt) || rt <= 0)
        return toast.error('Response target minutes required');
      if (!Number.isFinite(st) || st <= 0)
        return toast.error('Solution target minutes required');
      const overageDollars = parseFloat(overageRateAud);
      const overageCents =
        type === 'warranty'
          ? 0
          : Number.isFinite(overageDollars)
            ? Math.round(overageDollars * 100)
            : 0;
      create.mutate(
        {
          type,
          ...baseInput,
          monthlyHours: mh,
          overageRateCents: overageCents,
          responseTargetMinutes: rt,
          solutionTargetMinutes: st,
          businessHoursOnly,
          pauseOutsideHours,
        },
        {
          onSuccess: () => {
            toast.success('Contract created');
            onClose();
          },
        }
      );
    } else if (type === 'bau') {
      const th = parseInt(totalHours, 10);
      const dm = parseInt(durationMonths, 10);
      if (!Number.isFinite(th) || th <= 0)
        return toast.error('Total hours required');
      if (!Number.isFinite(dm) || dm <= 0)
        return toast.error('Duration in months required');
      const topUpDollars = parseFloat(topUpRateAud);
      const topUpCents = Number.isFinite(topUpDollars)
        ? Math.round(topUpDollars * 100)
        : null;
      create.mutate(
        {
          type: 'bau',
          ...baseInput,
          totalHours: th,
          durationMonths: dm,
          topUpsAllowed,
          topUpRateCents: topUpCents,
        },
        {
          onSuccess: () => {
            toast.success('Contract created');
            onClose();
          },
        }
      );
    } else {
      create.mutate(
        { type: 'internal', ...baseInput },
        {
          onSuccess: () => {
            toast.success('Contract created');
            onClose();
          },
        }
      );
    }
  }

  const users = usersQuery.data ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dialog-in-backdrop"
      onClick={create.isPending ? undefined : onClose}
    >
      <div
        className="w-full max-w-2xl mx-4 rounded-lg border border-ink-600 bg-ink-850 shadow-xl max-h-[92vh] overflow-y-auto dialog-in-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-contract-title"
      >
        <header className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-ink-700">
          <div>
            <h2
              id="new-contract-title"
              className="text-[15px] font-semibold text-ink-100"
            >
              {isDuplicating ? 'Duplicate contract' : 'New contract'}
            </h2>
            <p className="text-[12.5px] text-ink-300 mt-0.5">
              {isDuplicating ? (
                <>
                  for {organization.name} · based on{' '}
                  <span className="text-ink-200">
                    {duplicateFrom!.name}
                  </span>
                </>
              ) : (
                <>for {organization.name}</>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={create.isPending}
            className="text-ink-300 hover:text-ink-100 focus-ring rounded-md p-1 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-4">
          <Field label="Type">
            <div className="flex gap-1.5">
              {(['sla', 'warranty', 'bau', 'internal'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  disabled={create.isPending}
                  className={
                    'h-7 px-3 rounded-md text-[12.5px] focus-ring border ' +
                    (type === t
                      ? 'bg-ink-700 border-ink-500 text-ink-100'
                      : 'bg-ink-800 border-ink-600 text-ink-300 hover:text-ink-100')
                  }
                >
                  {t.toUpperCase()}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Name *">
            <Input
              type="text"
              value={name}
              disabled={create.isPending}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date *">
              <Input
                type="date"
                value={startDate}
                disabled={create.isPending}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Field>
            <Field label="End date *">
              <Input
                type="date"
                value={endDate}
                disabled={create.isPending}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Field>
          </div>

          <Field label="Account owner *">
            <select
              value={accountOwnerUserId}
              disabled={create.isPending}
              onChange={(e) => setAccountOwnerUserId(e.target.value)}
              className="h-8 px-2.5 text-[13px] bg-ink-800 border border-ink-600 rounded-md w-full focus-ring transition-colors"
            >
              <option value="">— select —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} · {u.email}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Slack webhook URL">
              <Input
                type="url"
                placeholder="https://hooks.slack.com/services/..."
                value={slackChannel}
                disabled={create.isPending}
                onChange={(e) => setSlackChannel(e.target.value)}
              />
            </Field>
            <Field label="Harvest project ID">
              <Input
                type="text"
                value={harvestProjectId}
                disabled={create.isPending}
                onChange={(e) => setHarvestProjectId(e.target.value)}
              />
            </Field>
          </div>

          {(type === 'sla' || type === 'warranty') && (
            <fieldset className="rounded-md border border-ink-600 p-3 space-y-3">
              <legend className="px-1.5 text-[11px] uppercase tracking-[0.06em] text-ink-300">
                {type === 'warranty' ? 'Warranty terms' : 'SLA terms'}
              </legend>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Monthly hours *">
                  <Input
                    type="number"
                    min="1"
                    value={monthlyHours}
                    disabled={create.isPending}
                    onChange={(e) => setMonthlyHours(e.target.value)}
                  />
                </Field>
                {type === 'sla' && (
                  <Field label="Overage rate A$/h">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={overageRateAud}
                      disabled={create.isPending}
                      onChange={(e) => setOverageRateAud(e.target.value)}
                    />
                  </Field>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Response target (min) *">
                  <Input
                    type="number"
                    min="1"
                    value={responseMinutes}
                    disabled={create.isPending}
                    onChange={(e) => setResponseMinutes(e.target.value)}
                  />
                </Field>
                <Field label="Solution target (min) *">
                  <Input
                    type="number"
                    min="1"
                    value={solutionMinutes}
                    disabled={create.isPending}
                    onChange={(e) => setSolutionMinutes(e.target.value)}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Toggle
                  label="Business hours only"
                  checked={businessHoursOnly}
                  onChange={setBusinessHoursOnly}
                  disabled={create.isPending}
                />
                <Toggle
                  label="Pause outside hours"
                  checked={pauseOutsideHours}
                  onChange={setPauseOutsideHours}
                  disabled={create.isPending}
                />
              </div>
            </fieldset>
          )}

          {type === 'bau' && (
            <fieldset className="rounded-md border border-ink-600 p-3 space-y-3">
              <legend className="px-1.5 text-[11px] uppercase tracking-[0.06em] text-ink-300">
                BAU terms
              </legend>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Total hours *">
                  <Input
                    type="number"
                    min="1"
                    value={totalHours}
                    disabled={create.isPending}
                    onChange={(e) => setTotalHours(e.target.value)}
                  />
                </Field>
                <Field label="Duration (months) *">
                  <Input
                    type="number"
                    min="1"
                    value={durationMonths}
                    disabled={create.isPending}
                    onChange={(e) => setDurationMonths(e.target.value)}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Toggle
                  label="Top-ups allowed"
                  checked={topUpsAllowed}
                  onChange={setTopUpsAllowed}
                  disabled={create.isPending}
                />
                <Field label="Top-up rate A$/h">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={topUpRateAud}
                    disabled={create.isPending || !topUpsAllowed}
                    onChange={(e) => setTopUpRateAud(e.target.value)}
                  />
                </Field>
              </div>
            </fieldset>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-ink-700">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create contract'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.06em] text-ink-300 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (b: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-[12.5px] text-ink-200 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 rounded border-ink-600 bg-ink-800 focus-ring"
      />
      {label}
    </label>
  );
}
